const STORAGE_KEY = 'msf_utm_v1';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type Utms = Record<UtmKey, string>;

const EMPTY_UTMS: Utms = {
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_content: '',
  utm_term: '',
};

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    const probe = '__msf_utm_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

function readFromUrl(): Partial<Utms> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Partial<Utms> = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v !== null) out[k] = String(v).toLowerCase();
  }
  return out;
}

function loadStored(): Partial<Utms> {
  const s = safeStorage();
  if (!s) return {};
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { values?: Partial<Utms>; expiresAt?: number };
    if (typeof parsed?.expiresAt === 'number' && parsed.expiresAt < Date.now()) {
      s.removeItem(STORAGE_KEY);
      return {};
    }
    return parsed?.values ?? {};
  } catch {
    return {};
  }
}

function persist(values: Partial<Utms>): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(
      STORAGE_KEY,
      JSON.stringify({
        values,
        expiresAt: Date.now() + TTL_MS,
        capturedAt: new Date().toISOString(),
      })
    );
  } catch {
    /* noop */
  }
}

export function captureUtmsFromUrl(): void {
  const fromUrl = readFromUrl();
  if (Object.keys(fromUrl).length === 0) return;
  const merged = { ...loadStored(), ...fromUrl };
  persist(merged);
}

export function getStoredUtms(): Utms {
  return { ...EMPTY_UTMS, ...loadStored() };
}

export function hasStoredUtms(): boolean {
  return Object.values(loadStored()).some((v) => typeof v === 'string' && v.length > 0);
}

/**
 * Tags an outbound share link so an arrival via that link is attributable.
 * captureUtmsFromUrl() (called on every page load, see main.tsx) already picks
 * up utm_* params from any URL and persists them for 30 days -- this is the
 * "tracked links out" half of that same pipeline, for share buttons which
 * previously generated bare, unattributed URLs.
 */
export function buildShareUrl(url: string, medium: string): string {
  try {
    const withUtm = new URL(url);
    withUtm.searchParams.set('utm_source', 'share');
    withUtm.searchParams.set('utm_medium', medium);
    withUtm.searchParams.set('utm_campaign', 'creator_share');
    return withUtm.toString();
  } catch {
    return url;
  }
}

/** utm_source value buildShareUrl() stamps on every outbound share link. */
export const SHARE_SOURCE = 'share';

/**
 * The INBOUND half of tracked sharing: did this visit arrive from one of our
 * own share links, and from where?
 *
 * buildShareUrl() has tagged links out for a while; nothing ever read them
 * back, which the portfolio plays list calls out as this module's classic
 * failure -- "often built and unfed". A share click on its own measures
 * enthusiasm. share_visit is the denominator that says whether the people
 * being sent are worth anything.
 *
 * Returns null for every other visit, so the caller fires NOTHING rather than
 * an event with empty properties: an "unknown" bucket that swallowed organic
 * traffic would be worse than no row at all.
 */
export function readShareArrival(
  search: string
): { medium: string; campaign: string } | null {
  const p = new URLSearchParams(search);
  if (p.get('utm_source') !== SHARE_SOURCE) return null;
  return {
    medium: p.get('utm_medium') || 'unknown',
    campaign: p.get('utm_campaign') || 'unknown',
  };
}
